import React, { useMemo, useState } from 'react';
import { Table, Form, Badge, Button } from 'react-bootstrap';
import { formatCurrency } from '../../utils/formatters';
import axios from 'axios';

const ReconcileComparisonView = ({ dbTransactions, pdfTransactions, selectedTxIds, onToggle, selectedAccount, showDiffOnly }) => {
  const [addingId, setAddingId] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? 'Invalid Date' : d.toLocaleDateString();
  };

  const handleQuickAdd = async (pdfTx, rowIdx) => {
    setAddingId(rowIdx);
    try {
        const projectId = window.location.pathname.split('/')[2]; // Extract from URL
        const isDeposit = pdfTx.amount >= 0;
        
        const payload = {
            date: new Date(pdfTx.date).toISOString().split('T')[0],
            description: pdfTx.description,
            amount: Math.abs(pdfTx.amount),
            fromAccount: isDeposit ? null : selectedAccount, // Outflow: From Bank
            toAccount: isDeposit ? selectedAccount : null,   // Inflow: To Bank
            category: 'Uncategorized'
        };

        await axios.post(`/api/projects/${projectId}/transactions`, payload);
        alert('Transaction added! Refreshing matching list...');
        window.location.reload(); // Refresh to trigger re-match
    } catch (err) {
        console.error(err);
        alert('Error adding transaction');
    } finally {
        setAddingId(null);
    }
  };

  const comparisonData = useMemo(() => {
    let pairs = [];
    const unmatchedDb = [...dbTransactions];
    const unmatchedPdf = [...pdfTransactions];

    const cleanDesc = (d) => (d || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    // Pass 1: High Confidence (Amount + Description + Date Proximity)
    for (let i = unmatchedPdf.length - 1; i >= 0; i--) {
      const pdf = unmatchedPdf[i];
      const pAmt = Math.abs(parseFloat(pdf.amount || 0)).toFixed(2);
      const pDate = new Date(pdf.date);
      const pDesc = cleanDesc(pdf.description);

      const dbIdx = unmatchedDb.findIndex(db => {
        const dbAmt = Math.abs(db.amount).toFixed(2);
        if (pAmt !== dbAmt) return false;

        const dbDate = new Date(db.date);
        const diffDays = Math.abs(pDate - dbDate) / (1000 * 60 * 60 * 24);
        if (diffDays > 4.5) return false;

        const dbDesc = cleanDesc(db.description);
        return pDesc.includes(dbDesc) || dbDesc.includes(pDesc);
      });

      if (dbIdx > -1) {
        pairs.push({ pdf, db: unmatchedDb[dbIdx], type: 'MATCH' });
        unmatchedDb.splice(dbIdx, 1);
        unmatchedPdf.splice(i, 1);
      }
    }

    // Pass 2: Description Priority (Amount + Description - ignoring date shift)
    for (let i = unmatchedPdf.length - 1; i >= 0; i--) {
      const pdf = unmatchedPdf[i];
      const pAmt = Math.abs(parseFloat(pdf.amount || 0)).toFixed(2);
      const pDesc = cleanDesc(pdf.description);
      if (pDesc.length < 5) continue;

      const dbIdx = unmatchedDb.findIndex(db => {
        const dbAmt = Math.abs(db.amount).toFixed(2);
        if (pAmt !== dbAmt) return false;
        const dbDesc = cleanDesc(db.description);
        return pDesc.includes(dbDesc) || dbDesc.includes(pDesc);
      });

      if (dbIdx > -1) {
        pairs.push({ pdf, db: unmatchedDb[dbIdx], type: 'MATCH' });
        unmatchedDb.splice(dbIdx, 1);
        unmatchedPdf.splice(i, 1);
      }
    }

    // Pass 3: Proximity Fallback (Amount + Date Proximity)
    for (let i = unmatchedPdf.length - 1; i >= 0; i--) {
      const pdf = unmatchedPdf[i];
      const pAmt = Math.abs(parseFloat(pdf.amount || 0)).toFixed(2);
      const pDate = new Date(pdf.date);

      const dbIdx = unmatchedDb.findIndex(db => {
        const dbAmt = Math.abs(db.amount).toFixed(2);
        if (pAmt !== dbAmt) return false;
        const dbDate = new Date(db.date);
        const diffDays = Math.abs(pDate - dbDate) / (1000 * 60 * 60 * 24);
        return diffDays <= 4.5;
      });

      if (dbIdx > -1) {
        pairs.push({ pdf, db: unmatchedDb[dbIdx], type: 'MATCH' });
        unmatchedDb.splice(dbIdx, 1);
        unmatchedPdf.splice(i, 1);
      }
    }

    // 4. Add remaining PDF Orphans
    unmatchedPdf.forEach(pdf => {
      pairs.push({ pdf, db: null, type: 'PDF_ONLY' });
    });

    // 5. Add remaining DB Orphans
    unmatchedDb.forEach(db => {
      pairs.push({ pdf: null, db, type: 'DB_ONLY' });
    });

    // Apply "Show Differences Only" filter
    if (showDiffOnly) {
        pairs = pairs.filter(p => p.type !== 'MATCH');
    }

    // Sort: Matches first (by date), then orphans
    return pairs.sort((a, b) => {
      if (a.type === 'MATCH' && b.type !== 'MATCH') return -1;
      if (b.type === 'MATCH' && a.type !== 'MATCH') return 1;
      
      const dateA = new Date(a.pdf?.date || a.db?.date);
      const dateB = new Date(b.pdf?.date || b.db?.date);
      return dateA - dateB;
    });
  }, [dbTransactions, pdfTransactions, showDiffOnly]);

  const getRowStyle = (type) => {
    switch (type) {
      case 'MATCH': return { backgroundColor: 'rgba(40, 167, 69, 0.05)' };
      case 'PDF_ONLY': return { backgroundColor: 'rgba(220, 53, 69, 0.05)' };
      case 'DB_ONLY': return { backgroundColor: 'rgba(255, 193, 7, 0.05)' };
      default: return {};
    }
  };

  return (
    <div className="reconcile-comparison-view shadow-sm border rounded bg-white overflow-hidden">
      <Table responsive hover className="mb-0 border-0">
        <thead className="bg-light">
          <tr>
            <th className="border-0 small text-uppercase py-3 text-center" style={{ width: '45%', borderRight: '1px solid #dee2e6' }}>Statement (PDF)</th>
            <th className="border-0 small text-uppercase py-3 text-center" style={{ width: '55%' }}>System (Database)</th>
          </tr>
        </thead>
        <tbody>
          {comparisonData.length === 0 ? (
            <tr>
              <td colSpan="2" className="text-center py-5 text-muted">
                {showDiffOnly ? 'No differences found! Everything matches.' : 'No transactions to display.'}
              </td>
            </tr>
          ) : (
            comparisonData.map((row, idx) => {
                const pdf = row.pdf;
                const db = row.db;

                let dbDisplayAmount = 0;
                if (db) {
                   const isOutflow = db.fromAccount && (db.fromAccount._id === selectedAccount || db.fromAccount === selectedAccount);
                   dbDisplayAmount = isOutflow ? -Math.abs(db.amount) : Math.abs(db.amount);
                }

                return (
                <tr key={idx} style={getRowStyle(row.type)}>
                    {/* PDF Column */}
                    <td className="align-middle border-right" style={{ borderRight: '1px solid #dee2e6' }}>
                    {pdf ? (
                        <div className="d-flex justify-content-between align-items-center px-2">
                        <div>
                            <div className="small font-weight-bold">{formatDate(pdf.date)}</div>
                            <div className="extra-small text-muted text-truncate" style={{ maxWidth: '250px' }}>{pdf.description}</div>
                        </div>
                        <div className={`font-weight-bold ${pdf.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatCurrency(pdf.amount)}
                        </div>
                        </div>
                    ) : (
                        <div className="text-center text-muted italic extra-small">Not on Statement</div>
                    )}
                    </td>

                    {/* DB Column */}
                    <td className="align-middle" onClick={() => db && onToggle(db._id)} style={{ cursor: db ? 'pointer' : 'default' }}>
                    {db ? (
                        <div className="d-flex align-items-center px-2">
                        <Form.Check 
                            type="checkbox" 
                            checked={selectedTxIds.has(db._id)} 
                            readOnly 
                            className="mr-3"
                        />
                        <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="small font-weight-bold">{formatDate(db.date)}</div>
                                    <div className="extra-small text-muted text-truncate" style={{ maxWidth: '250px' }}>{db.description}</div>
                                </div>
                                <div className="text-right">
                                    <div className={`font-weight-bold ${dbDisplayAmount >= 0 ? 'text-success' : 'text-danger'}`}>
                                        {formatCurrency(dbDisplayAmount)}
                                    </div>
                                    <Badge variant="light" className="border extra-small" style={{ fontSize: '0.6rem' }}>{db.category}</Badge>
                                </div>
                            </div>
                        </div>
                        </div>
                    ) : (
                        <div className="text-center py-1">
                            <span className="text-danger italic extra-small d-block mb-1">
                                <i className="fas fa-exclamation-triangle mr-1"></i> Missing from Database
                            </span>
                            <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="extra-small py-0 px-2"
                                onClick={(e) => { e.stopPropagation(); handleQuickAdd(pdf, idx); }}
                                disabled={addingId === idx}
                            >
                                {addingId === idx ? 'Adding...' : 'Quick Add to System'}
                            </Button>
                        </div>
                    )}
                    </td>
                </tr>
                );
            })
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ReconcileComparisonView;
